import { useEffect, useMemo, useState } from "react";
import { Pencil, Trash2, X, Search, UserPlus, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { hasPerm } from "../lib/permissions";

export default function UsersManagement() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // user or "new" or null

  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterDept, setFilterDept] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const canCreate = hasPerm(user, "users", "create");
  const canEdit = hasPerm(user, "users", "edit");
  const canDelete = hasPerm(user, "users", "delete");

  const refresh = async () => {
    try {
      const [{ data: u }, { data: r }, { data: d }] = await Promise.all([
        api.get("/users"),
        api.get("/roles"),
        api.get("/config/departments"),
      ]);
      setUsers(u);
      setRoles(r);
      setDepartments(d);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    return users.filter((u) => {
      if (search) {
        const q = search.toLowerCase();
        if (!u.email.toLowerCase().includes(q) && !(u.name || "").toLowerCase().includes(q)) return false;
      }
      if (filterRole && (u.role_name || "") !== filterRole) return false;
      if (filterDept) {
        const arr = Array.isArray(u.departments) ? u.departments.map((d) => String(d).toUpperCase()) : [];
        const legacy = (u.department || "").trim().toUpperCase();
        const has = arr.length ? arr.includes(filterDept.toUpperCase()) : (legacy === filterDept.toUpperCase());
        if (!has) return false;
      }
      if (filterStatus && (u.status || "active") !== filterStatus) return false;
      return true;
    });
  }, [users, search, filterRole, filterDept, filterStatus]);

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete user "${u.email}"?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`User "${u.email}" deleted`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const activeCount = users.filter((u) => (u.status || "active") === "active").length;
  const inactiveCount = users.length - activeCount;

  return (
    <div data-testid="users-page" className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#00D4FF] mb-1">User Management</div>
          <h1 className="font-heading text-3xl font-bold">Users</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {users.length} total · {activeCount} active · {inactiveCount} inactive
          </p>
        </div>
        {canCreate && (
          <button
            data-testid="new-user-btn"
            onClick={() => setEditing("new")}
            className="flex items-center gap-2 bg-[#00D4FF] text-[#0A0B10] px-4 py-2 text-sm font-semibold tracking-wider uppercase hover:bg-[#00B8D4] transition-colors"
          >
            <UserPlus size={16} /> New User
          </button>
        )}
      </header>

      <div className="bg-[#12141D] border border-border p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="relative md:col-span-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="users-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="w-full bg-[#0A0B10] border border-border pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[#00D4FF]"
          />
        </div>
        <select
          data-testid="users-filter-role"
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
          className="bg-[#0A0B10] border border-border px-3 py-2 text-sm focus:outline-none focus:border-[#00D4FF]"
        >
          <option value="">All Roles</option>
          {roles.map((r) => <option key={r.id} value={r.name}>{r.name}</option>)}
        </select>
        <select
          data-testid="users-filter-dept"
          value={filterDept}
          onChange={(e) => setFilterDept(e.target.value)}
          className="bg-[#0A0B10] border border-border px-3 py-2 text-sm focus:outline-none focus:border-[#00D4FF]"
        >
          <option value="">All Departments</option>
          {departments.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select
          data-testid="users-filter-status"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="bg-[#0A0B10] border border-border px-3 py-2 text-sm focus:outline-none focus:border-[#00D4FF]"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      <div className="bg-[#12141D] border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#1A1C28] text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
            <tr>
              <th className="text-left py-3 px-4 font-normal">User</th>
              <th className="text-left py-3 px-4 font-normal">Role</th>
              <th className="text-left py-3 px-4 font-normal">Departments</th>
              <th className="text-left py-3 px-4 font-normal">Plant</th>
              <th className="text-left py-3 px-4 font-normal">Status</th>
              <th className="text-right py-3 px-4 font-normal">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">No users match the filters</td></tr>
            )}
            {filtered.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-border/60 hover:bg-[#1A1C28]/40">
                <td className="py-3 px-4">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 bg-[#1A1C28] border border-border flex items-center justify-center text-[11px] font-mono text-[#00D4FF]">
                      {(u.name || u.email).slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <div className="font-medium text-white">{u.name || "—"}</div>
                      <div className="text-xs text-muted-foreground">{u.email}</div>
                    </div>
                  </div>
                </td>
                <td className="py-3 px-4">{u.role_name || <span className="text-muted-foreground">—</span>}</td>
                <td className="py-3 px-4">
                  {(() => {
                    const arr = Array.isArray(u.departments) ? u.departments : [];
                    if (arr.length === 0) {
                      const legacy = (u.department || "").trim();
                      if (!legacy || legacy === "All Departments") {
                        return <span className="text-muted-foreground">All departments</span>;
                      }
                      return <span className="px-2 py-0.5 text-[10px] tracking-widest uppercase border border-border bg-[#0A0B10]">{legacy}</span>;
                    }
                    return (
                      <div className="flex flex-wrap gap-1">
                        {arr.map((d) => (
                          <span key={d} className="px-2 py-0.5 text-[10px] tracking-widest uppercase border border-border bg-[#0A0B10]">{d}</span>
                        ))}
                      </div>
                    );
                  })()}
                </td>
                <td className="py-3 px-4">
                  <span className="text-[10px] tracking-widest uppercase font-mono text-muted-foreground">
                    {(u.plant_scope || "BOTH") === "BOTH" ? "SLC + Mexico"
                      : (u.plant_scope === "SLC") ? "SLC only" : "Mexico only"}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <span className={`text-[10px] tracking-widest uppercase px-2 py-1 border ${
                    (u.status || "active") === "active"
                      ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
                      : "bg-zinc-500/10 border-zinc-500/30 text-zinc-400"
                  }`}>
                    {u.status || "active"}
                  </span>
                </td>
                <td className="py-3 px-4 text-right">
                  <div className="inline-flex gap-1">
                    {canEdit && (
                      <button
                        data-testid={`edit-user-${u.id}`}
                        onClick={() => setEditing(u)}
                        className="p-1.5 text-muted-foreground hover:text-[#00D4FF] hover:bg-[#1A1C28] transition-colors"
                      >
                        <Pencil size={14} />
                      </button>
                    )}
                    {canDelete && u.id !== user?.id && (
                      <button
                        data-testid={`delete-user-${u.id}`}
                        onClick={() => handleDelete(u)}
                        className="p-1.5 text-muted-foreground hover:text-red-400 hover:bg-[#1A1C28] transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <UserEditor
          roles={roles}
          departments={departments}
          user={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}

function UserEditor({ roles, departments, user, onClose, onSaved }) {
  // Split name → first + last
  const parts = (user?.name || "").trim().split(/\s+/);
  const initialFirst = parts[0] || "";
  const initialLast = parts.slice(1).join(" ") || "";
  const [firstName, setFirstName] = useState(initialFirst);
  const [lastName, setLastName] = useState(initialLast);
  const [email, setEmail] = useState(user?.email || "");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [roleId, setRoleId] = useState(user?.role_id || (roles.find((r) => r.name === "Administrator")?.id || roles[0]?.id || ""));
  // Multi-dept access scope (empty array = "All Departments"). Migrate legacy single
  // `department` string to the new array if present.
  const _initialDepts = (() => {
    const arr = user?.departments;
    if (Array.isArray(arr) && arr.length) return arr.map((d) => String(d).toUpperCase());
    const legacy = (user?.department || "").trim().toUpperCase();
    return (legacy && legacy !== "ALL DEPARTMENTS") ? [legacy] : [];
  })();
  const [selectedDepts, setSelectedDepts] = useState(_initialDepts);
  const [plantScope, setPlantScope] = useState((user?.plant_scope || "BOTH").toUpperCase());
  const [saving, setSaving] = useState(false);
  const isEdit = !!user;

  // Dept options exclude the synthetic "All Departments" entry.
  const deptOptions = useMemo(
    () => (departments || []).filter((d) => d && d !== "All Departments"),
    [departments],
  );
  const toggleDept = (d) => {
    const up = String(d).toUpperCase();
    setSelectedDepts((curr) =>
      curr.includes(up) ? curr.filter((x) => x !== up) : [...curr, up]
    );
  };

  const save = async () => {
    if (!firstName.trim() || !lastName.trim()) { toast.error("First and Last name are required"); return; }
    if (!email.trim()) { toast.error("Email is required"); return; }
    if (!roleId) { toast.error("Role is required"); return; }
    if (!isEdit) {
      if (!password || password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    } else if (password && password.length < 8) {
      toast.error("Password must be at least 8 characters"); return;
    }
    const fullName = `${firstName.trim()} ${lastName.trim()}`;
    setSaving(true);
    try {
      if (isEdit) {
        const payload = {
          name: fullName, role_id: roleId,
          departments: selectedDepts,
          plant_scope: plantScope,
        };
        if (password) payload.password = password;
        await api.put(`/users/${user.id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/users", {
          email: email.trim(),
          name: fullName,
          password,
          role_id: roleId,
          departments: selectedDepts,
          plant_scope: plantScope,
          status: "active",
        });
        toast.success("User created");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 lg:pl-[17rem]" onClick={onClose}>
      <div
        data-testid="user-editor-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: "calc(100vh - 2rem)" }}
        className="bg-[#12141D] border border-border w-full max-w-xl flex flex-col"
      >
        <header className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <h2 className="font-heading text-xl font-bold">{isEdit ? "Edit User" : "New User"}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-white" data-testid="close-user-editor">
            <X size={20} />
          </button>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2 block">First Name *</label>
              <input
                data-testid="user-first-name-input"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="John"
                className="w-full bg-[#0A0B10] border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-[#00D4FF]"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2 block">Last Name *</label>
              <input
                data-testid="user-last-name-input"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Doe"
                className="w-full bg-[#0A0B10] border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-[#00D4FF]"
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2 block">Email *</label>
            <input
              data-testid="user-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isEdit}
              type="email"
              placeholder="john@company.com"
              className="w-full bg-[#0A0B10] border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-[#00D4FF] disabled:opacity-60"
            />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2 block">
              {isEdit ? "New Password (leave blank to keep)" : "Password *"}
            </label>
            <div className="relative">
              <input
                data-testid="user-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type={showPwd ? "text" : "password"}
                autoComplete="new-password"
                placeholder="Minimum 8 characters"
                className="w-full bg-[#0A0B10] border border-border pl-3 pr-10 py-2.5 text-sm focus:outline-none focus:border-[#00D4FF]"
              />
              <button
                type="button"
                data-testid="user-password-toggle"
                onClick={() => setShowPwd((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-[#00D4FF]"
                title={showPwd ? "Hide password" : "Show password"}
              >
                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2 block">Role *</label>
              <select
                data-testid="user-role-select"
                value={roleId}
                onChange={(e) => setRoleId(e.target.value)}
                className="w-full bg-[#0A0B10] border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-[#00D4FF]"
              >
                {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2 block">Plant Access</label>
              <div className="flex gap-2" data-testid="user-plant-scope">
                {[
                  { v: "SLC",  label: "SLC only" },
                  { v: "MEX",  label: "Mexico only" },
                  { v: "BOTH", label: "Both" },
                ].map((opt) => {
                  const active = plantScope === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      data-testid={`plant-scope-${opt.v.toLowerCase()}`}
                      onClick={() => setPlantScope(opt.v)}
                      className={`flex-1 px-2 py-2 text-[10px] tracking-widest uppercase border transition-colors ${
                        active
                          ? "bg-[#00D4FF] text-[#0A0B10] border-[#00D4FF] font-semibold"
                          : "bg-[#0A0B10] border-border text-muted-foreground hover:text-white"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div>
            <label className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2 block">
              Departments Access
              <span className="ml-2 text-muted-foreground/70 normal-case tracking-normal">
                ({selectedDepts.length === 0 ? "All departments" : `${selectedDepts.length} selected`})
              </span>
            </label>
            <div className="flex flex-wrap gap-2" data-testid="user-depts-chips">
              {deptOptions.map((d) => {
                const up = String(d).toUpperCase();
                const on = selectedDepts.includes(up);
                return (
                  <button
                    key={d}
                    type="button"
                    data-testid={`user-dept-${up.toLowerCase()}`}
                    onClick={() => toggleDept(d)}
                    className={`px-3 py-1.5 text-[10px] tracking-widest uppercase border transition-colors ${
                      on
                        ? "bg-[#00D4FF] text-[#0A0B10] border-[#00D4FF] font-semibold"
                        : "bg-[#0A0B10] border-border text-muted-foreground hover:text-white"
                    }`}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-muted-foreground mt-2">
              Leave empty to grant access to all departments. Pick one or more to scope this user to those dept tabs only.
            </p>
          </div>
        </div>

        <footer className="px-6 py-4 border-t border-border flex items-center justify-end gap-2 shrink-0 bg-[#12141D]">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm border border-border hover:bg-[#1A1C28] transition-colors"
            data-testid="cancel-user-btn"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="save-user-btn"
            className="px-6 py-2 text-sm bg-[#00D4FF] text-[#0A0B10] font-semibold tracking-wider uppercase hover:bg-[#00B8D4] disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </footer>
      </div>
    </div>
  );
}
